import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiGet, apiPostJson, apiPutJson, apiDelete, apiDownload, ApiError } from '../api/client';
import { formatMkd, formatDate, todayIso, monthAgoIso } from '../lib/format';
import FilterBar, { btn } from '../components/FilterBar';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';

/** Borxhet — debts owed to the group. Ports frmBorxhet and frmBorxhetAdd. */

interface BorxhRow {
  ID: number;
  startDate: string | null;
  endDate: string | null;
  kompania: string | null;
  emri: string | null;
  kontakt: string | null;
  shuma: number;
  pershkrimi: string | null;
}

const emptyForm = {
  startDate: todayIso(),
  endDate: todayIso(),
  kompania: '',
  emri: '',
  kontakt: '',
  shuma: '',
  pershkrimi: '',
};

export default function Borxhet() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(monthAgoIso);
  const [endDate, setEndDate] = useState(todayIso);
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<BorxhRow | null>(null);

  const listQuery = useQuery({
    queryKey: ['borxhet', search, startDate, endDate],
    queryFn: () =>
      apiGet<{ rows: BorxhRow[]; total: number }>('/borxhet', { search, startDate, endDate }),
    placeholderData: (prev) => prev,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['borxhet'] });
  }

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDelete(`/borxhet/${id}`),
    onSuccess: () => {
      toast.success('Rreshti u fshi me sukses.');
      refresh();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Fshirja dështoi.'),
  });

  async function openEdit(row: BorxhRow | null) {
    setFormError(null);
    if (!row) {
      setForm(emptyForm);
      setEditing({ id: null });
      return;
    }
    setForm({
      startDate: row.startDate?.slice(0, 10) ?? todayIso(),
      endDate: row.endDate?.slice(0, 10) ?? todayIso(),
      kompania: row.kompania ?? '',
      emri: row.emri ?? '',
      kontakt: row.kontakt ?? '',
      shuma: String(row.shuma ?? ''),
      pershkrimi: row.pershkrimi ?? '',
    });
    setEditing({ id: row.ID });
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    const amount = Number(form.shuma);
    if (form.shuma.trim() === '' || !Number.isFinite(amount)) {
      setFormError('Ju lutem vendosni një shumë të vlefshme.');
      return;
    }
    if (form.endDate < form.startDate) {
      setFormError('Data e mbarimit nuk mund të jetë para datës së fillimit.');
      return;
    }

    const body = {
      startDate: form.startDate,
      endDate: form.endDate,
      kompania: form.kompania.trim() || undefined,
      emri: form.emri.trim() || undefined,
      kontakt: form.kontakt.trim() || undefined,
      shuma: amount,
      pershkrimi: form.pershkrimi.trim() || undefined,
    };

    try {
      if (editing?.id) await apiPutJson(`/borxhet/${editing.id}`, body);
      else await apiPostJson('/borxhet', body);
      setEditing(null);
      toast.success('Të dhënat u ruajtën me sukses!');
      refresh();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Ruajtja dështoi.');
    }
  }

  const rows = listQuery.data?.rows ?? [];
  const inputClass =
    'w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100';

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Borxhet</h1>
        <p className="text-sm text-muted">Detyrimet e regjistruara sipas datës së fillimit</p>
      </div>

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        startDate={startDate}
        onStartDateChange={setStartDate}
        endDate={endDate}
        onEndDateChange={setEndDate}
        searchLabel="Kërko kompani, emër ose kontakt"
      >
        <button
          type="button"
          onClick={() =>
            apiDownload('/borxhet/eksport', { search, startDate, endDate }).catch((err) =>
              toast.error(err instanceof ApiError ? err.message : 'Eksportimi dështoi.'),
            )
          }
          disabled={rows.length === 0}
          className={btn.secondary}
        >
          Eksporto Excel
        </button>
        <button type="button" onClick={() => openEdit(null)} className={btn.primary}>
          Shto borxh
        </button>
      </FilterBar>

      <div className="overflow-hidden rounded-xl bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-semibold">Prej</th>
                <th className="px-4 py-3 font-semibold">Deri</th>
                <th className="px-4 py-3 font-semibold">Kompania</th>
                <th className="px-4 py-3 font-semibold">Emri</th>
                <th className="px-4 py-3 font-semibold">Kontakti</th>
                <th className="px-4 py-3 font-semibold">Përshkrimi</th>
                <th className="px-4 py-3 text-right font-semibold">Shuma</th>
                <th className="px-4 py-3 text-right font-semibold">Veprime</th>
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-muted">
                    Duke ngarkuar…
                  </td>
                </tr>
              )}
              {!listQuery.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-muted">
                    Nuk ka borxhe për këtë periudhë.
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.ID} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(row.startDate)}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(row.endDate)}</td>
                  <td className="px-4 py-2.5">{row.kompania ?? '—'}</td>
                  <td className="px-4 py-2.5">{row.emri ?? '—'}</td>
                  <td className="px-4 py-2.5 text-muted">{row.kontakt ?? '—'}</td>
                  <td className="max-w-xs truncate px-4 py-2.5 text-muted" title={row.pershkrimi ?? ''}>
                    {row.pershkrimi ?? ''}
                  </td>
                  <td className="tnum px-4 py-2.5 text-right font-medium">{formatMkd(row.shuma)}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                      >
                        Ndrysho
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleting(row)}
                        className="rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                      >
                        Fshij
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
          <span className="text-sm text-muted">
            Shuma totale nga {formatDate(startDate)} deri më {formatDate(endDate)}:
          </span>
          <span className="tnum text-lg font-semibold text-ink">
            {formatMkd(listQuery.data?.total ?? 0)}
          </span>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
          <form
            onSubmit={handleSave}
            className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl"
          >
            <h2 className="mb-4 text-lg font-semibold">
              {editing.id ? 'Ndrysho borxhin' : 'Shto borxh'}
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="bx-start">
                  Prej
                </label>
                <input
                  id="bx-start"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                  required
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="bx-end">
                  Deri
                </label>
                <input
                  id="bx-end"
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                  required
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="bx-kompania">
                  Kompania
                </label>
                <input
                  id="bx-kompania"
                  value={form.kompania}
                  onChange={(e) => setForm({ ...form, kompania: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="bx-emri">
                  Emri
                </label>
                <input
                  id="bx-emri"
                  value={form.emri}
                  onChange={(e) => setForm({ ...form, emri: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="bx-kontakt">
                  Kontakti
                </label>
                <input
                  id="bx-kontakt"
                  value={form.kontakt}
                  onChange={(e) => setForm({ ...form, kontakt: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="bx-shuma">
                  Shuma
                </label>
                <input
                  id="bx-shuma"
                  inputMode="decimal"
                  value={form.shuma}
                  onChange={(e) => setForm({ ...form, shuma: e.target.value.replace(/[^\d.]/g, '') })}
                  required
                  className={`tnum ${inputClass}`}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="bx-pershkrimi">
                  Përshkrimi
                </label>
                <textarea
                  id="bx-pershkrimi"
                  rows={3}
                  value={form.pershkrimi}
                  onChange={(e) => setForm({ ...form, pershkrimi: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>

            {formError && (
              <div role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                {formError}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Anulo
              </button>
              <button type="submit" className={btn.primary}>
                Ruaj
              </button>
            </div>
          </form>
        </div>
      )}

      {deleting && (
        <ConfirmDialog
          title="Konfirmo fshirjen"
          message="A jeni i sigurt që dëshironi të fshini këtë rresht?"
          confirmLabel="Fshij"
          destructive
          busy={deleteMutation.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            deleteMutation.mutate(deleting.ID);
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}
